import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { extractProps } from 'ctm-util-functions';
import { Button } from '../../button/button';

/**
 * _SubmitButton React Component - used to submit a form outside of the journey, e.g. Call me back form
 * @extends React.Component
 * @returns {Node} a Button Component
 */
class _SubmitButton extends Component {

    constructor(props) {
        super(props);
        this.state = {
            submissionInProgress: false
        };
    }

    /**
     * Determine if the component should re-render based on the nextProps, the only change that should cause a re-render
     * is if the transitioningToNewPage property is different.
     * @param  {Object} nextProps  - The incoming set of props.
     * @returns {Boolean} - true if the component should re-render.
     */
    shouldComponentUpdate = (nextProps) => {
        const { transitioningToNewPage } = this.props;
        return transitioningToNewPage !== nextProps.transitioningToNewPage;
    };

    /**
     * On each update, set the internal SubmissionInProgress to false if api call finished
     * @param {Object} nextProps  - The incoming set of props.
     */
    componentWillUpdate = (nextProps) => {
        if (!nextProps.transitioningToNewPage) {
            // eslint-disable-next-line react/no-will-update-set-state
            this.setState({ submissionInProgress: false });
        }
    };

    /**
     * Click handler that only submit the form if formSubmission is NOT already in progress
     */
    onClick = () => {
        const { transitioningToNewPage, validateAndSubmit } = this.props;
        if (!transitioningToNewPage) {
            validateAndSubmit();
            this.setState({ submissionInProgress: true });
        }
    };

    /**
     * Renders a Button component with clickHandler
     * @returns {Node} - a Button component
     */
    render = () => {
        const { buttonText, pageName, UIComponent, variant } = this.props;
        const { submissionInProgress } = this.state;
        return (
            <Button
                data-id={`${pageName}.submitButton`}
                label={buttonText}
                loading={submissionInProgress}
                loadingText="Submitting"
                UIComponent={UIComponent}
                variant={variant}
                onClick={this.onClick}
            />
        );
    };

}

_SubmitButton.propTypes = {
    buttonText: PropTypes.string.isRequired,
    pageName: PropTypes.string.isRequired, // @todo it really should be formName, but in Schema each form is called page...
    transitioningToNewPage: PropTypes.bool, // if the form submission in progress
    validateAndSubmit: PropTypes.func,
    UIComponent: PropTypes.string.isRequired,
    variant: PropTypes.string
};

/**
 * Extract transitioningToNewPage from the store
 * @param {Object} state - Redux store state.
 * @return {Object} - The state attrs to add to props.
 */
export const mapStateToProps = (state) => {
    return {
        transitioningToNewPage: state.getIn(['page', 'transitioningToNewPage'])
    };
};

/**
 * Create functions to validate and submit the form
 * @param {Function} dispatch - Redux dispatch method.
 * @param {Object} props - Props passed to the component.
 * @return {Object} - Function to dispatch actions that are passed as props.
 */
export const mapDispatchToProps = (dispatch, props) => ({
    validateAndSubmit: () => {
        const { api: apiSchema, pageName, stateKey, stateProps } = props;
        // Prepare extra payload for form submission, if any
        const requestPayload = extractProps(stateProps, apiSchema.extraPayload || {});

        dispatch({
            type: 'VALIDATE_AND_SUBMIT',
            pageName,
            apiSchema,
            stateKey,
            requestPayload
        });
    }
});

const SubmitButton = connect(mapStateToProps, mapDispatchToProps)(_SubmitButton);
export { _SubmitButton, SubmitButton };
